import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchMenuCounts,
  fetchPaginatedAddons,
  fetchPaginatedCategories,
  fetchPaginatedMenuItems,
  searchMenuItems,
} from '../../services/menuServicePaginated';
import {
  Addon,
  FilterMenuItemsParams,
  MenuCategory,
  MenuItem,
} from '../../types/menu';
import {
  AddonsFilterParams,
  CategoriesFilterParams,
  MenuCountsResponse,
  MenuItemsFilterParams,
  PaginatedResponse,
} from '../../types/pagination';

export const fetchMenuItemsByCategory = createAsyncThunk<
  MenuItem[],
  FilterMenuItemsParams,
  { rejectValue: string }
>('menu/fetchMenuItemsByCategory', async (_params, { rejectWithValue }) => {
  return rejectWithValue('Use TanStack Query useMenuItems hook instead');
});

export const fetchPaginatedMenuItemsThunk = createAsyncThunk<
  PaginatedResponse<MenuItem>,
  MenuItemsFilterParams,
  { rejectValue: string }
>('menu/fetchPaginatedMenuItems', async (params, { rejectWithValue }) => {
  try {
    const response = await fetchPaginatedMenuItems(params);

    if (!response.success || !response.data) {
      return rejectWithValue(response.error || 'Failed to fetch menu items');
    }

    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch menu items',
    );
  }
});

export const fetchPaginatedCategoriesThunk = createAsyncThunk<
  PaginatedResponse<MenuCategory>,
  CategoriesFilterParams,
  { rejectValue: string }
>('menu/fetchPaginatedCategories', async (params, { rejectWithValue }) => {
  try {
    const response = await fetchPaginatedCategories(params);

    if (!response.success || !response.data) {
      return rejectWithValue(response.error || 'Failed to fetch categories');
    }

    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch categories',
    );
  }
});

export const fetchPaginatedAddonsThunk = createAsyncThunk<
  PaginatedResponse<Addon>,
  AddonsFilterParams,
  { rejectValue: string }
>('menu/fetchPaginatedAddons', async (params, { rejectWithValue }) => {
  try {
    const response = await fetchPaginatedAddons(params);

    if (!response.success || !response.data) {
      return rejectWithValue(response.error || 'Failed to fetch addons');
    }

    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch addons',
    );
  }
});

export const fetchMenuCountsThunk = createAsyncThunk<
  MenuCountsResponse,
  string,
  { rejectValue: string }
>('menu/fetchMenuCounts', async (outletId, { rejectWithValue }) => {
  try {
    const response = await fetchMenuCounts(outletId);

    if (!response.success || !response.data) {
      return rejectWithValue(response.error || 'Failed to fetch menu counts');
    }

    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch menu counts',
    );
  }
});

export const searchMenuItemsThunk = createAsyncThunk<
  PaginatedResponse<MenuItem>,
  { outletId: string; search: string; page?: number; pageSize?: number },
  { rejectValue: string }
>('menu/searchMenuItems', async (params, { rejectWithValue }) => {
  try {
    const response = await searchMenuItems(params);

    if (!response.success || !response.data) {
      return rejectWithValue(response.error || 'Failed to search menu items');
    }

    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to search menu items',
    );
  }
});
